import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import blogService from '../../services/blogService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import ArticleContent from './components/ArticleContent';
import RelatedArticles from './components/RelatedArticles';
import ShareButtons from './components/ShareButtons';

const BlogArticleView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) {
      loadArticle();
    }
  }, [slug]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      setError('');
      
      const articleData = await blogService?.getArticleBySlug(slug);
      setArticle(articleData);

      // Increment view count
      await blogService?.incrementViewCount(articleData?.id);

      // Load related articles from same category
      if (articleData?.categoryId) {
        const related = await blogService?.getPublishedArticles(articleData?.categoryId, 'newest');
        // Filter out current article and limit to 3
        const filtered = related?.filter(a => a?.id !== articleData?.id)?.slice(0, 3);
        setRelatedArticles(filtered);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleBackToBlog = () => {
    navigate('/financial-blog');
  };

  const handleRelatedArticleClick = (articleSlug) => {
    navigate(`/blog-article-view/${articleSlug}`);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Financial Blog', path: '/financial-blog' },
    { label: article?.title || 'Article', path: '' }
  ];

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Article - Rovyn Group</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="container mx-auto px-16 py-24 max-w-4xl">
            <div className="flex items-center justify-center py-64">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-16"></div>
                <p className="text-muted-foreground">Loading article...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Helmet>
          <title>Article Not Found - Rovyn Group</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="container mx-auto px-16 py-24 max-w-4xl">
            <div className="text-center py-64">
              <Icon name="FileX" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-24" />
              <h2 className="text-2xl font-semibold text-foreground mb-12">Article Not Found</h2>
              <p className="text-muted-foreground mb-24">
                {error || 'The article you are looking for does not exist or has been removed.'}
              </p>
              <button
                onClick={handleBackToBlog}
                className="px-24 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth min-h-[44px]"
              >
                Back to Blog
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article?.title} - Rovyn Group Financial Blog</title>
        <meta name="description" content={article?.excerpt || article?.title} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-16 py-24 max-w-4xl">
          <Breadcrumbs items={breadcrumbItems} />

          {/* Back Button */}
          <button
            onClick={handleBackToBlog}
            className="flex items-center gap-8 text-muted-foreground hover:text-foreground transition-smooth mb-24 min-h-[44px]"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Blog</span>
          </button>

          {/* Article Header */}
          <article className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
            {/* Featured Image */}
            {article?.featuredImageUrl && (
              <div className="relative h-96 overflow-hidden">
                <img 
                  src={article?.featuredImageUrl} 
                  alt={article?.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-24 md:p-32">
              {/* Category Badge */}
              {article?.categoryName && (
                <div className="mb-16">
                  <span className="inline-block px-12 py-6 bg-primary/10 text-primary text-sm font-medium rounded">
                    {article?.categoryName}
                  </span>
                </div>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-card-foreground mb-20">
                {article?.title}
              </h1>

              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-16 pb-24 mb-24 border-b border-border">
                <div className="flex items-center gap-8 text-sm text-muted-foreground">
                  <Icon name="User" size={16} />
                  <span>{article?.authorName}</span>
                </div>
                <div className="flex items-center gap-8 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span>{formatDate(article?.publishedAt)}</span>
                </div>
                {article?.readingTimeMinutes && (
                  <div className="flex items-center gap-8 text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} />
                    <span>{article?.readingTimeMinutes} min read</span>
                  </div>
                )}
                {article?.viewsCount > 0 && (
                  <div className="flex items-center gap-8 text-sm text-muted-foreground">
                    <Icon name="Eye" size={16} />
                    <span>{article?.viewsCount} views</span>
                  </div>
                )}
              </div>

              {/* Share Buttons */}
              <ShareButtons article={article} />

              {/* Article Content */}
              <ArticleContent content={article?.content} />
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles?.length > 0 && (
            <RelatedArticles 
              articles={relatedArticles} 
              onArticleClick={handleRelatedArticleClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BlogArticleView;